// tslint:disable: max-classes-per-file
// tslint:disable: member-ordering
// tslint:disable: no-inferrable-types
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { BackendObjectRef } from '../lib/backend-integration.service';
import { State } from '../lib/state.model';
import { Transition, TransitionConfirmationInfo } from '../lib/transition.model';
import { SelectControlGroup } from '../lib/workflow-editor.store';

interface ExtraFields {
  sourceNew?: string;
  targetNew?: string;
}
type TransitionFieldsToPick = 'autoTransition'|
                              'timeTriggeredTransition'|
                              'timeExpiration'|
                              'confirmation'|
                              'requireValidForm'|
                              'message'|
                              'name'|
                              'roles'|
                              'source'|
                              'target';
type TransitionFields = Pick<Transition, TransitionFieldsToPick>;
type WorkflowTransitionFormValue = {[P in keyof (TransitionFields & ExtraFields)]: any};
export type WorkflowTransitionFormResult = TransitionFields;

export class WorkflowTransitionForm {

  public readonly states: State[];
  public readonly roles: SelectControlGroup<BackendObjectRef>[];
  public readonly form: FormGroup;
  public readonly transition?: Transition;

  public isAddingSource: boolean = false;
  public isAddingTarget: boolean = false;
  public isRenamingSource: boolean = false;
  public isRenamingTarget: boolean = false;

  public get canRenameSource(): boolean {
    return this.form.value.source !== null && !this.isAddingSource && !this.isRenamingSource;
  }

  public get canRenameTarget(): boolean {
    return this.form.value.target !== null && !this.isAddingTarget && !this.isRenamingTarget;
  }

  public static isNewStateOption(ref: BackendObjectRef|undefined): boolean {
    if (!ref) { return false; }
    return ref.id === this.NewStateOption.id;
  }

  private static NewStateOption: State = new State('-1', 'NEW STATE...');

  private static transitionToValue(transition: Transition|undefined): WorkflowTransitionFormValue {
    if (!transition) { return this.FORM_DEFAULT_VALUE; }
    return {...this.FORM_DEFAULT_VALUE, ...transition};
  }

  private static FORM_DEFAULT_VALUE: WorkflowTransitionFormValue = {
    autoTransition: false,
    timeTriggeredTransition: false,
    timeExpiration: 0,
    confirmation: {
      requireConfirmation: true,
      title: '',
      description: $localize `Would you like to continue?`,
      cancelButtonTitle: $localize `Cancel`,
      continueButtonTitle: $localize `Continue`,
    },
    message: '',
    name: '',
    requireValidForm: true,
    roles: [],
    source: undefined,
    sourceNew: undefined,
    target: undefined,
    targetNew: undefined
  };

  private static buildFormGroup(fb: FormBuilder,
                                value: WorkflowTransitionFormValue): FormGroup {
    const controls: {[P in keyof WorkflowTransitionFormValue]: any} = {
      name:             [value.name, Validators.required],
      roles:            [value.roles, Validators.required],
      source:           [value.source, Validators.required],
      target:           [value.target, Validators.required],
      message:          [value.message],
      autoTransition:   [value.autoTransition],
      timeTriggeredTransition: [value.timeTriggeredTransition],
      timeExpiration:   [value.timeExpiration, Validators.min(0)],
      requireValidForm: [value.requireValidForm],
      confirmation: WorkflowTransitionForm.buildConfirmationGroup(fb, value.confirmation)
    };

    return fb.group(controls, {});
  }

  private static buildConfirmationGroup(fb: FormBuilder, value: TransitionConfirmationInfo): FormGroup {
    const controls: {[K in keyof TransitionConfirmationInfo]: any} = {
      requireConfirmation: [value.requireConfirmation],
      title:               [value.title],
      description:         [value.description],
      cancelButtonTitle:   [value.cancelButtonTitle],
      continueButtonTitle: [value.continueButtonTitle],
    };
    return fb.group(controls);
  }

  constructor(fb: FormBuilder,
              transition: Transition|undefined,
              states: State[],
              roles: SelectControlGroup<BackendObjectRef>[]) {
    this.roles = roles;
    this.transition = transition;
    this.states = [WorkflowTransitionForm.NewStateOption, ...states];

    const value = WorkflowTransitionForm.transitionToValue(transition);
    this.form = WorkflowTransitionForm.buildFormGroup(fb, value);

    const sourceControl = this.form.get('source') as FormControl;
    sourceControl.valueChanges.subscribe(change => {
      if (this.isRenamingSource) { this.cancelRenameSourceState(); }

     WorkflowTransitionForm.isNewStateOption(change)
       ? this.startAddingSource()
       : this.cancelAddingSource();
    });

    const targetControl = this.form.get('target') as FormControl;
    targetControl.valueChanges.subscribe(change => {
      if (this.isRenamingTarget) { this.cancelRenameTargetState(); }

     WorkflowTransitionForm.isNewStateOption(change)
       ? this.startAddingTarget()
       : this.cancelAddingTarget();
    });
  }

  public get dirty(): boolean {
    return this.form.dirty;
  }

  public isValid(path?: string | (string | number)[]): boolean {
    if (!path) { return this.form.valid; }
    const control = this.form.get(path);
    return control ? control.valid : false;
  }

  public isInvalid(path?: string | (string | number)[]): boolean {
    if (!path) { return this.form.invalid; }
    const control = this.form.get(path);
    return control ? control.invalid : true;
  }

  public hasError(errorCode: string, path?: (string|number)[] | string): boolean {
    return this.form.hasError(errorCode, path);
  }

  public get isConfirmationRequired(): boolean {
    return this.form.value.confirmation.requireConfirmation;
  }

  public get isTimeTriggered(): boolean {
    return this.form.value.timeTriggeredTransition;
  }

  public get isAddingTransition(): boolean {
    return !this.transition;
  }

  public get value(): WorkflowTransitionFormResult {
    const result: WorkflowTransitionFormResult = {
      ...this.form.value,
    };
    if (this.isAddingSource) {
      result.source = new State(undefined, this.form.value.sourceNew);
    }
    if (this.isAddingSource) {
      result.source = new State(undefined, this.form.value.sourceNew);
    } else if (this.isRenamingSource) {
      result.source = new State(this.form.value.source.id,
                                this.form.value.sourceNew);
    } else {
      result.source = this.form.value.source;
    }

    if (this.isAddingTarget) {
      result.target = new State(undefined, this.form.value.targetNew);
    } else if (this.isRenamingTarget) {
      result.target = new State(this.form.value.target.id,
                                this.form.value.targetNew);
    } else {
      result.target = this.form.value.target;
    }

    return result;
  }

  public addSourceNewControl() {
    const control = new FormControl('', Validators.required);
    this.form.addControl('sourceNew', control);
  }

  public removeSourceNewControl() {
    this.form.removeControl('sourceNew');
  }

  public startAddingSource() {
    this.addSourceNewControl();
    this.isAddingSource = true;
  }

  public cancelAddingSource() {
    this.isAddingSource = false;
    this.removeSourceNewControl();
  }

  public startRenameSourceState() {
    this.isRenamingSource = true;
    this.addSourceNewControl();
  }

  public cancelRenameSourceState() {
    this.isRenamingSource = false;
    this.removeSourceNewControl();
  }

  public addTargetNewControl() {
    const control = new FormControl('', Validators.required);
    this.form.addControl('targetNew', control);
  }

  public removeTargetNewControl() {
    this.form.removeControl('targetNew');
  }

  public startAddingTarget() {
    this.addTargetNewControl();
    this.isAddingTarget = true;
  }

  public cancelAddingTarget() {
    this.isAddingTarget = false;
    this.removeTargetNewControl();
  }

  public startRenameTargetState() {
    this.isRenamingTarget = true;
    this.addTargetNewControl();
  }

  public cancelRenameTargetState() {
    this.isRenamingTarget = false;
    this.removeTargetNewControl();
  }
}
